import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { HereGeocodeItemType } from '@/api/apis/here-maps';
import { api } from '@/api';
import { extractGeocodePoints } from '@/utils/helpers/location.helpers';
import { queryClient } from '@/queries';
import { hereMapsQueries } from '@/queries/here-maps';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { DEFAULT_COUNTRY_ISO3 } from '@/constants/location.constants';

type UseAddressSearchProps = {
  address: string;
  countryCode?: string;
  stateName?: string;
};

const fetchAutosuggestAddresses = async (q: string, country: string, state?: string) => {
  try {
    const geocode = await queryClient.ensureQueryData(
      hereMapsQueries.getGeocode({ country, state: state || undefined }),
    );

    const { bboxPoints, centerPoints } = extractGeocodePoints(geocode);

    const results = await api.hereMaps.getAddressAutosuggest({
      query: q,
      country,
      centerPoints,
      bbox: state ? bboxPoints : undefined,
    });

    return results.items;
  } catch (e) {
    // eslint-disable-next-line
    console.error(e);
  }

  return [];
};

export const useAddressSearch = (
  { address, countryCode = 'US', stateName = '' }: UseAddressSearchProps,
  init: boolean,
) => {
  const { countriesByIso2 } = useCountriesData();
  const selectedCountry = countriesByIso2[countryCode]?.alpha3 ?? DEFAULT_COUNTRY_ISO3;
  const [addressesList, setAddressesList] = useState<HereGeocodeItemType[]>([]);
  const [query, setQuery] = useState('');

  useDebounce(
    () => {
      if (init && address.length > 2) {
        setQuery(address);
      }
    },
    500,
    [address, countryCode, init],
  );

  const initSearch = useCallback(
    async (q: string) => {
      const results = await fetchAutosuggestAddresses(q, selectedCountry, stateName);

      setAddressesList(results);
    },
    [setAddressesList, selectedCountry, stateName],
  );

  useEffect(() => {
    if (query) {
      initSearch(query);
    }
  }, [initSearch, query]);

  return {
    list: addressesList,
  };
};
